import { useState } from "react";

export default function HeroSection() {
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      const response = await fetch("https://formspree.io/f/xojzbvwg", {
        method: "POST",
        body: (() => { const p = new URLSearchParams(data as unknown as Record<string, string>); p.append("formulario","Presupuesto (Hero)"); p.append("_subject","Nuevo presupuesto (Hero) — Persianas Málaga"); return p; })(),
        headers: { "Content-Type": "application/x-www-form-urlencoded", "Accept": "application/json" },
      });
      if (response.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("idle");
      }
    } catch {
      setFormStatus("idle");
    }
  };

  return (
    <section className="relative w-full min-h-[420px] sm:min-h-[440px] lg:min-h-[520px] bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/40 overflow-hidden">
      <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row lg:items-center">
        {/* Text content */}
        <div className="flex-1 flex items-center justify-center lg:justify-start px-4 md:px-6 lg:px-10 py-4 md:py-8 lg:py-12 order-2 lg:order-1">
          <div className="max-w-xl mx-auto lg:mx-0 w-full">
            <div className="inline-flex items-center gap-2 bg-primary-500/10 text-primary-700 text-xs md:text-sm font-semibold px-4 py-2 rounded-full mb-4 md:mb-5 border border-primary-200/50">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flashlight-fill text-primary-600"></i></span>
              Servicio en 24h · Más de 2.500 reparaciones este año
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-foreground-950 mb-3 md:mb-4 leading-tight font-heading">
              Técnico especialista en persianas<br /><span className="text-primary-500">en Málaga</span> — reparamos, motorizamos, instalamos
            </h1>
            <p className="text-sm md:text-lg text-foreground-600 mb-3 max-w-md">
              Servicio urgente 24/48h · Presupuesto gratis · 16 barrios de Málaga capital
            </p>
            <p className="hidden sm:block text-sm md:text-base text-primary-600 mb-6 md:mb-8 max-w-md font-medium">
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-map-pin-2-fill"></i></span>
              Centro, La Malagueta, El Palo, Pedregalejo, Teatinos, El Limonar, Churriana, Carretera de Cádiz, Cruz de Humilladero, Bailén-Miraflores, Ciudad Jardín, Puerto de la Torre, Campanillas, Huelin, Soliva y El Cónsul
            </p>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowForm(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-background-50 font-bold text-sm md:text-base px-6 py-3 sm:px-8 sm:py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
              >
                Pide presupuesto gratis
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </button>
              <a
                href="tel:+34951120770"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-800 font-semibold text-sm md:text-base px-6 py-3 sm:px-8 sm:py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer border border-background-200"
              >
                <span className="w-5 h-5 flex items-center justify-center text-primary-500"><i className="ri-phone-fill"></i></span>
                951 12 07 70
              </a>
            </div>

            <div className="mt-4 md:mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-2 md:gap-5 text-xs md:text-sm text-foreground-600">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-accent-400"></i></span>
                4.9/5 · +10 años · 2 años de garantía · Málaga capital
              </span>
            </div>
          </div>
        </div>

        {/* Hero image — mobile */}
        <div className="block lg:hidden w-full h-60 sm:h-72 relative order-1 lg:order-2 flex-shrink-0">
          <img
            alt="Técnico profesional reparando una persiana en Málaga capital"
            className="w-full h-full object-cover object-center"
            loading="eager"
            src="/images/004-hero-mobile-malaga-v4.webp"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/30 via-transparent to-background-50/50" />
        </div>

        {/* Hero image — desktop */}
        <div className="hidden lg:block w-[46%] xl:w-[44%] h-[520px] max-h-[520px] relative order-1 lg:order-2 lg:self-center flex-shrink-0">
          <img
            alt="Técnico profesional reparando una persiana en Málaga capital"
            className="w-full h-full object-cover object-top"
            loading="eager"
            src="/images/006-hero-malaga-tecnico-v2.webp"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-background-50" />
          <div className="absolute top-6 right-6 z-10">
            <div className="bg-accent-400 text-secondary-900 font-extrabold text-base px-6 py-3.5 rounded-2xl shadow-2xl shadow-accent-400/30 animate-pulse cursor-default flex items-center gap-2.5 border-2 border-accent-300">
              <div className="w-9 h-9 flex items-center justify-center bg-secondary-900 rounded-full flex-shrink-0">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-price-tag-3-fill text-accent-400 text-lg"></i></span>
              </div>
              <span className="whitespace-nowrap tracking-tight">PRESUPUESTO GRATIS</span>
            </div>
          </div>
        </div>

        {/* Mobile badge */}
        <div className="block lg:hidden absolute top-3 right-3 z-10">
          <div className="bg-accent-400 text-secondary-900 font-extrabold text-[9px] sm:text-xs px-2 py-1.5 sm:px-3 sm:py-2 rounded-xl shadow-2xl shadow-accent-400/30 animate-pulse cursor-default flex items-center gap-1 sm:gap-1.5 border-2 border-accent-300">
            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-secondary-900 rounded-full flex-shrink-0">
              <span className="w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center"><i className="ri-price-tag-3-fill text-accent-400 text-[10px] sm:text-xs"></i></span>
            </div>
            <span className="whitespace-nowrap tracking-tight">PRESUPUESTO GRATIS</span>
          </div>
        </div>
      </div>

      {/* Quote form modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-background-50 rounded-2xl p-6 md:p-8 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground-950 font-heading">Pide presupuesto gratis</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-700 cursor-pointer">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-close-line text-lg"></i></span>
              </button>
            </div>
            {formStatus === "success" ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <span className="w-6 h-6 flex items-center justify-center"><i className="ri-check-line text-primary-600 text-xl"></i></span>
                </div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">¡Presupuesto enviado!</p>
                <p className="text-xs text-foreground-600">Te contactaremos en menos de 15 minutos.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="hero-name" className="block text-sm font-medium text-foreground-800 mb-1.5">Nombre</label>
                  <input id="hero-name" name="name" required className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400" placeholder="Tu nombre" />
                </div>
                <div>
                  <label htmlFor="hero-phone" className="block text-sm font-medium text-foreground-800 mb-1.5">Teléfono</label>
                  <input id="hero-phone" name="phone" type="tel" required className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400" placeholder="Tu teléfono" />
                </div>
                <div>
                  <label htmlFor="hero-service" className="block text-sm font-medium text-foreground-800 mb-1.5">¿Qué necesitas?</label>
                  <select id="hero-service" name="service" required className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 text-foreground-900 appearance-none cursor-pointer">
                    <option value="">Selecciona un servicio</option>
                    <option value="Persiana">Persiana</option>
                    <option value="Mosquiteras">Mosquiteras</option>
                    <option value="Motorización">Motorización</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <button type="submit" disabled={formStatus === "submitting"} className="w-full py-3.5 px-6 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-primary-300 disabled:to-primary-300 text-background-50 font-semibold text-sm rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20">
                  {formStatus === "submitting" ? "Enviando..." : "Solicitar presupuesto"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}